import { DeleteRounded, EditRounded } from "@mui/icons-material";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";

import { Box, Button, Popover, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import useAPI from "../../api/hooks/useAPI";
import useColors from "../../api/hooks/useColors";
import useMSSP from "../../api/hooks/useMSSP";
import { platformUsersKeys } from "../../api/queryKeys";
import { Toast } from "../../components/Toast";
import ConfirmationDialog from "../../components/dialogs/ConfirmationDialog";
import AddNewTenantModal from "./AddNewTenantModal";

export default function EditOrDeleteTenantButton({
  msspId,
  tenant,
  disabledDelete,
}) {
  const { editMSSPTenant, deleteMSSPTenant, trackEvent } = useAPI();
  const colors = useColors();
  const { currentTenantId, switchTenant } = useMSSP();

  const queryClient = useQueryClient();
  const [anchorEl, setAnchorEl] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const formInitialValues = {
    companyName: tenant.company_name,
    companyDomain: tenant.customer_id,
    timezone: tenant.timezone,
    importance: tenant.importance,
    labels: tenant.labels,
    sla: {
      Critical: tenant.sla?.critical,
      High: tenant.sla?.high,
      Medium: tenant.sla?.medium,
      Low: tenant.sla?.low,
    },
  };

  const handleClick = (event) => {
    trackEvent(
      `User clicked on the three dots icon for tenant ${tenant.customer_id}`
    );
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    trackEvent(
      `User closed the delete and edit model for tenant ${tenant.customer_id}`
    );
    setAnchorEl(null);
  };

  const handleDelete = () => {
    trackEvent(`User clicked on delete for tenant ${tenant.customer_id}`);
    setOpenDeleteDialog(true);
    handleClose();
  };

  const handleEdit = () => {
    trackEvent(`User clicked on edit for tenant ${tenant.customer_id}`);
    setOpenEditModal(true);
    handleClose();
  };

  const onSubmitEdit = async (values) => {
    const newTenant = {
      company_name: values.companyName,
      company_domain: values.companyDomain,
      importance: values.importance,
      labels: values.labels,
      sla: values.sla,
    };

    try {
      await editMSSPTenant(newTenant);
      Toast.success("Tenant edited successfully");
      queryClient.invalidateQueries({
        queryKey: platformUsersKeys.msspTenants(),
      });
      trackEvent(`User edited tenant ${tenant.customer_id}`);
      onCloseEditModal();
    } catch (error) {
      Toast.error(`Failed to edit tenant ${tenant.customer_id}`);
    }
  };

  const onCloseEditModal = () => {
    setOpenEditModal(false);
    trackEvent("User close the edit tenant dialog");
  };

  const onCloseDeleteDialog = () => {
    trackEvent(
      `User cancelled delete tenant ${tenant.customer_id} in the delete dialog`
    );
    setOpenDeleteDialog(false);
  };

  const handleDeleteConfirm = () => {
    trackEvent(`User confirmed to delete tenant ${tenant.customer_id}`);
    setOpenDeleteDialog(false);
    handleDeleteTenant(tenant.customer_id);
  };

  const handleDeleteTenant = async (tenant_id) => {
    try {
      await deleteMSSPTenant(tenant_id);
      queryClient.invalidateQueries({
        queryKey: platformUsersKeys.msspTenants(),
      });

      //   If the tenant being deleted is the current tenant, switch to the main MSSP tenant
      if (tenant_id === currentTenantId) {
        switchTenant(msspId);
      }

      trackEvent(`User deleted a tenant`);
    } catch (error) {
      Toast.error("Failed to delete tenant");
    }
  };

  return (
    <>
      <MoreVertRoundedIcon
        onClick={(event) => handleClick(event)}
        sx={{
          cursor: "pointer",
          fontSize: "20px",
          color: colors.grey_700,
        }}
      />
      <Popover
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: "9px",
              boxShadow: "0px 2px 4px 0px rgba(0, 0, 0, 0.1)",
            },
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            padding: "3px",
          }}
        >
          <Button
            aria-label="edit"
            onClick={handleEdit}
            startIcon={
              <EditRounded sx={{ color: colors.grey_700, fontSize: 24 }} />
            }
            sx={{
              justifyContent: "flex-start",
              padding: "6px 12px",
              textTransform: "none",
              fontSize: "12px",
              borderRadius: "8px",
              "&:hover": {
                backgroundColor: colors.grey_200,
              },
            }}
          >
            <Typography variant="caption">Edit details</Typography>
          </Button>
          <Button
            aria-label="delete"
            onClick={handleDelete}
            disabled={disabledDelete}
            startIcon={
              <DeleteRounded
                sx={{
                  color: disabledDelete ? colors.grey_400 : colors.grey_700,
                  fontSize: 24,
                }}
              />
            }
            sx={{
              justifyContent: "flex-start",
              padding: "6px 12px",
              textTransform: "none",
              fontSize: "12px",
              borderRadius: "8px",
              "&:hover": {
                backgroundColor: colors.grey_200,
              },
            }}
          >
            <Typography variant="caption">Delete tenant</Typography>
          </Button>
        </Box>
      </Popover>
      <ConfirmationDialog
        openDialog={openDeleteDialog}
        handleDialogClose={onCloseDeleteDialog}
        handleConfirm={handleDeleteConfirm}
        dialogContentFirstLine="Are you sure you want to delete this tenant?"
        dialogContentSecondLine="Deleting this tenant will permanently remove all associated data. This action cannot be undone and may take up to 48 hours to complete."
        dialogContentThirdLine={`Tenant: ${tenant.customer_id}`}
        confirmationButtonText="Remove"
        destructive={true}
      />
      <AddNewTenantModal
        open={openEditModal}
        onClose={onCloseEditModal}
        title="Edit Tenant"
        onSubmit={onSubmitEdit}
        loadingText="Editing..."
        initialValues={formInitialValues}
        isDisabledCompanyDomain={true}
        isDisabledTimezone={true}
      />
    </>
  );
}
